import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * NOTE:::: COPIED FROM PRODUCT-PRICE.JS
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class VariantMetafields extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { lengthContainer, widthContainer, heightContainer } = this.refs;
        // Find the new product-price element in the updated HTML
        const newVariantMetafields = html.querySelector(
          `variant-metafields[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newVariantMetafields) return;

        // Update length container
        const newLength = newVariantMetafields.querySelector('[ref="lengthContainer"]');
        if (newLength && lengthContainer) {
          lengthContainer.replaceWith(newLength);
        }

        // Update width container
        const newWidth = newVariantMetafields.querySelector('[ref="widthContainer"]');
        if (newWidth && widthContainer) {
          widthContainer.replaceWith(newWidth);
        }

        // Update height container
        const newHeight = newVariantMetafields.querySelector('[ref="heightContainer"]');
        if (newHeight && heightContainer) {
          heightContainer.replaceWith(newHeight);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-metafields')) {
  customElements.define('variant-metafields', VariantMetafields);
}
